import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixApiInfraV1WixAiExternalGatewayUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ai-gateway_prompts';

/**
 * Generate different content such as text, image, and video according to Prompt object configuration
 * Prompt object is used for all generate content request configuration, such as what vendor, what model and what parameters to use.
 */
export function generateContentByPromptObject(
  payload: object
): RequestOptionsFactory<any> {
  function __generateContentByPromptObject({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'prompt.openAiChatCompletionRequest.temperature' },
          { path: 'prompt.openAiChatCompletionRequest.topP' },
          { path: 'prompt.openAiChatCompletionRequest.presencePenalty' },
          { path: 'prompt.openAiChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.azureChatCompletionRequest.temperature' },
          { path: 'prompt.azureChatCompletionRequest.topP' },
          { path: 'prompt.azureChatCompletionRequest.presencePenalty' },
          { path: 'prompt.azureChatCompletionRequest.frequencyPenalty' },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.temperature',
          },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.topP',
          },
          { path: 'prompt.anthropicClaudeRequest.temperature' },
          { path: 'prompt.anthropicClaudeRequest.topP' },
          { path: 'prompt.googleAnthropicClaudeRequest.temperature' },
          { path: 'prompt.googleAnthropicClaudeRequest.topP' },
          { path: 'prompt.invokeAnthropicModelRequest.temperature' },
          { path: 'prompt.invokeAnthropicModelRequest.topP' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.temperature' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.topP' },
          { path: 'prompt.stabilityAiTextToImageRequest.textPrompts.weight' },
          { path: 'prompt.stabilityAiStableDiffusionRequest.strength' },
          {
            path: 'prompt.blackForestLabsGenerateImageRequest.imagePromptStrength',
          },
          { path: 'prompt.replicateCreatePredictionRequest.fluxPulid.trueCfg' },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.idWeight',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.loraStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.controlStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.imageToImageStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.prunaaiZImageTurbo.guidanceScale',
          },
          { path: 'prompt.stabilityAiEditWithPromptRequest.creativity' },
          { path: 'prompt.runwareTextToImageRequest.strength' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.trueCfg' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.idWeight' },
          {
            path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.guidanceScale',
          },
          { path: 'prompt.googleCreateChatCompletionRequest.temperature' },
          { path: 'prompt.googleCreateChatCompletionRequest.topP' },
          { path: 'prompt.googleCreateChatCompletionRequest.presencePenalty' },
          { path: 'prompt.googleCreateChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.temperature' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.topP' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.presencePenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.frequencyPenalty' },
          { path: 'prompt.openAiResponsesRequest.temperature' },
          { path: 'prompt.openAiResponsesRequest.topP' },
          { path: 'prompt.azureOpenAiResponsesRequest.temperature' },
          { path: 'prompt.azureOpenAiResponsesRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.temperature' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.presencePenalty' },
          {
            path: 'prompt.fireworksInvokeCustomOpenAiRequest.frequencyPenalty',
          },
          { path: 'prompt.llamaModelRequest.temperature' },
          { path: 'prompt.llamaModelRequest.topP' },
          { path: 'prompt.runwareTextToImageRequest.cfgScale' },
          { path: 'prompt.runwareTextToImageRequest.loraModels.weight' },
          { path: 'prompt.mlPlatformLlamaModelRequest.temperature' },
          { path: 'prompt.mlPlatformLlamaModelRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.temperature' },
          { path: 'prompt.perplexityChatCompletionRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.topK' },
          { path: 'prompt.perplexityChatCompletionRequest.presencePenalty' },
          { path: 'prompt.perplexityChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.runwareVideoInferenceRequest.cfgScale' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.api_infra.v1.prompt_proxy',
      method: 'POST' as any,
      methodFqn:
        'wix.api_infra.v1.WixAiExternalGateway.GenerateContentByPromptObject',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixApiInfraV1WixAiExternalGatewayUrl({
        protoPath: '/v1/generate-content-by-prompt-object',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.safetyRatings.probabilityScore',
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.safetyRatings.severityScore',
              },
              {
                path: 'response.googleGenerateImageResponse.predictions.safetyAttributes.scores',
                isRepeated: true,
              },
              { path: 'response.openAiResponsesResponse.temperature' },
              { path: 'response.azureOpenAiResponsesResponse.temperature' },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.temperature',
              },
              { path: 'materializedPrompt.openAiChatCompletionRequest.topP' },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.temperature',
              },
              { path: 'materializedPrompt.azureChatCompletionRequest.topP' },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.temperature',
              },
              {
                path: 'materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.topP',
              },
              { path: 'materializedPrompt.anthropicClaudeRequest.temperature' },
              { path: 'materializedPrompt.anthropicClaudeRequest.topP' },
              {
                path: 'materializedPrompt.googleAnthropicClaudeRequest.temperature',
              },
              { path: 'materializedPrompt.googleAnthropicClaudeRequest.topP' },
              {
                path: 'materializedPrompt.invokeAnthropicModelRequest.temperature',
              },
              { path: 'materializedPrompt.invokeAnthropicModelRequest.topP' },
              {
                path: 'materializedPrompt.amazonConverseRequest.inferenceConfig.temperature',
              },
              {
                path: 'materializedPrompt.amazonConverseRequest.inferenceConfig.topP',
              },
              {
                path: 'materializedPrompt.stabilityAiTextToImageRequest.textPrompts.weight',
              },
              {
                path: 'materializedPrompt.stabilityAiStableDiffusionRequest.strength',
              },
              {
                path: 'materializedPrompt.blackForestLabsGenerateImageRequest.imagePromptStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.trueCfg',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.idWeight',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.guidanceScale',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.loraStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.guidanceScale',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.controlStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.imageToImageStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.prunaaiZImageTurbo.guidanceScale',
              },
              {
                path: 'materializedPrompt.stabilityAiEditWithPromptRequest.creativity',
              },
              { path: 'materializedPrompt.runwareTextToImageRequest.strength' },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.trueCfg',
              },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.idWeight',
              },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.guidanceScale',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.temperature',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.topP',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.temperature',
              },
              { path: 'materializedPrompt.mlPlatformOpenAiRawRequest.topP' },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.frequencyPenalty',
              },
              { path: 'materializedPrompt.openAiResponsesRequest.temperature' },
              { path: 'materializedPrompt.openAiResponsesRequest.topP' },
              {
                path: 'materializedPrompt.azureOpenAiResponsesRequest.temperature',
              },
              { path: 'materializedPrompt.azureOpenAiResponsesRequest.topP' },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.temperature',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.topP',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.frequencyPenalty',
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.groundingSupports.confidenceScores',
                isRepeated: true,
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.retrievalMetadata.googleSearchDynamicRetrievalScore',
              },
              { path: 'materializedPrompt.llamaModelRequest.temperature' },
              { path: 'materializedPrompt.llamaModelRequest.topP' },
              { path: 'materializedPrompt.runwareTextToImageRequest.cfgScale' },
              {
                path: 'materializedPrompt.runwareTextToImageRequest.loraModels.weight',
              },
              {
                path: 'materializedPrompt.mlPlatformLlamaModelRequest.temperature',
              },
              { path: 'materializedPrompt.mlPlatformLlamaModelRequest.topP' },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.temperature',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.topP',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.topK',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.runwareVideoInferenceRequest.cfgScale',
              },
            ],
          },
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.searchEntryPoint.sdkBlob',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __generateContentByPromptObject;
}

/**
 * Generate text according to Prompt object configuration.
 * Prompt object is used for all generate text request configuration, such as what vendor, what model and what parameters to use.
 * The response is streamed back in chunks.
 */
export function generateTextByPromptObjectStreamed(
  payload: object
): RequestOptionsFactory<any> {
  function __generateTextByPromptObjectStreamed({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'prompt.openAiChatCompletionRequest.temperature' },
          { path: 'prompt.openAiChatCompletionRequest.topP' },
          { path: 'prompt.openAiChatCompletionRequest.presencePenalty' },
          { path: 'prompt.openAiChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.azureChatCompletionRequest.temperature' },
          { path: 'prompt.azureChatCompletionRequest.topP' },
          { path: 'prompt.azureChatCompletionRequest.presencePenalty' },
          { path: 'prompt.azureChatCompletionRequest.frequencyPenalty' },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.temperature',
          },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.topP',
          },
          { path: 'prompt.anthropicClaudeRequest.temperature' },
          { path: 'prompt.anthropicClaudeRequest.topP' },
          { path: 'prompt.googleAnthropicClaudeRequest.temperature' },
          { path: 'prompt.googleAnthropicClaudeRequest.topP' },
          { path: 'prompt.invokeAnthropicModelRequest.temperature' },
          { path: 'prompt.invokeAnthropicModelRequest.topP' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.temperature' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.topP' },
          { path: 'prompt.stabilityAiTextToImageRequest.textPrompts.weight' },
          { path: 'prompt.stabilityAiStableDiffusionRequest.strength' },
          {
            path: 'prompt.blackForestLabsGenerateImageRequest.imagePromptStrength',
          },
          { path: 'prompt.replicateCreatePredictionRequest.fluxPulid.trueCfg' },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.idWeight',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.loraStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.controlStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.imageToImageStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.prunaaiZImageTurbo.guidanceScale',
          },
          { path: 'prompt.stabilityAiEditWithPromptRequest.creativity' },
          { path: 'prompt.runwareTextToImageRequest.strength' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.trueCfg' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.idWeight' },
          {
            path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.guidanceScale',
          },
          { path: 'prompt.googleCreateChatCompletionRequest.temperature' },
          { path: 'prompt.googleCreateChatCompletionRequest.topP' },
          { path: 'prompt.googleCreateChatCompletionRequest.presencePenalty' },
          { path: 'prompt.googleCreateChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.temperature' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.topP' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.presencePenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.frequencyPenalty' },
          { path: 'prompt.openAiResponsesRequest.temperature' },
          { path: 'prompt.openAiResponsesRequest.topP' },
          { path: 'prompt.azureOpenAiResponsesRequest.temperature' },
          { path: 'prompt.azureOpenAiResponsesRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.temperature' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.presencePenalty' },
          {
            path: 'prompt.fireworksInvokeCustomOpenAiRequest.frequencyPenalty',
          },
          { path: 'prompt.llamaModelRequest.temperature' },
          { path: 'prompt.llamaModelRequest.topP' },
          { path: 'prompt.runwareTextToImageRequest.cfgScale' },
          { path: 'prompt.runwareTextToImageRequest.loraModels.weight' },
          { path: 'prompt.mlPlatformLlamaModelRequest.temperature' },
          { path: 'prompt.mlPlatformLlamaModelRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.temperature' },
          { path: 'prompt.perplexityChatCompletionRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.topK' },
          { path: 'prompt.perplexityChatCompletionRequest.presencePenalty' },
          { path: 'prompt.perplexityChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.runwareVideoInferenceRequest.cfgScale' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.api_infra.v1.prompt_proxy',
      method: 'POST' as any,
      methodFqn:
        'wix.api_infra.v1.WixAiExternalGateway.GenerateTextByPromptObjectStreamed',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixApiInfraV1WixAiExternalGatewayUrl({
        protoPath: '/v1/generate-by-prompt-object-streamed',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'googleGeminiStreamChunk.candidates.safetyRatings.probabilityScore',
              },
              {
                path: 'googleGeminiStreamChunk.candidates.safetyRatings.severityScore',
              },
              {
                path: 'googleGeminiStreamChunk.candidates.groundingMetadata.groundingSupports.confidenceScores',
                isRepeated: true,
              },
              {
                path: 'googleGeminiStreamChunk.candidates.groundingMetadata.retrievalMetadata.googleSearchDynamicRetrievalScore',
              },
            ],
          },
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [
              {
                path: 'googleGeminiStreamChunk.candidates.groundingMetadata.searchEntryPoint.sdkBlob',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __generateTextByPromptObjectStreamed;
}

export function generateContentByPromptObjectAsync(
  payload: object
): RequestOptionsFactory<any> {
  function __generateContentByPromptObjectAsync({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'prompt.openAiChatCompletionRequest.temperature' },
          { path: 'prompt.openAiChatCompletionRequest.topP' },
          { path: 'prompt.openAiChatCompletionRequest.presencePenalty' },
          { path: 'prompt.openAiChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.azureChatCompletionRequest.temperature' },
          { path: 'prompt.azureChatCompletionRequest.topP' },
          { path: 'prompt.azureChatCompletionRequest.presencePenalty' },
          { path: 'prompt.azureChatCompletionRequest.frequencyPenalty' },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.temperature',
          },
          {
            path: 'prompt.googleGeminiGenerateContentRequest.generationConfig.topP',
          },
          { path: 'prompt.anthropicClaudeRequest.temperature' },
          { path: 'prompt.anthropicClaudeRequest.topP' },
          { path: 'prompt.googleAnthropicClaudeRequest.temperature' },
          { path: 'prompt.googleAnthropicClaudeRequest.topP' },
          { path: 'prompt.invokeAnthropicModelRequest.temperature' },
          { path: 'prompt.invokeAnthropicModelRequest.topP' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.temperature' },
          { path: 'prompt.amazonConverseRequest.inferenceConfig.topP' },
          { path: 'prompt.stabilityAiTextToImageRequest.textPrompts.weight' },
          { path: 'prompt.stabilityAiStableDiffusionRequest.strength' },
          {
            path: 'prompt.blackForestLabsGenerateImageRequest.imagePromptStrength',
          },
          { path: 'prompt.replicateCreatePredictionRequest.fluxPulid.trueCfg' },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.idWeight',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxPulid.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.loraStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.guidanceScale',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.controlStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.fluxDevControlnet.imageToImageStrength',
          },
          {
            path: 'prompt.replicateCreatePredictionRequest.prunaaiZImageTurbo.guidanceScale',
          },
          { path: 'prompt.stabilityAiEditWithPromptRequest.creativity' },
          { path: 'prompt.runwareTextToImageRequest.strength' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.trueCfg' },
          { path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.idWeight' },
          {
            path: 'prompt.mlPlatformGenerateImageRequest.fluxPulid.guidanceScale',
          },
          { path: 'prompt.googleCreateChatCompletionRequest.temperature' },
          { path: 'prompt.googleCreateChatCompletionRequest.topP' },
          { path: 'prompt.googleCreateChatCompletionRequest.presencePenalty' },
          { path: 'prompt.googleCreateChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.temperature' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.topP' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.presencePenalty' },
          { path: 'prompt.mlPlatformOpenAiRawRequest.frequencyPenalty' },
          { path: 'prompt.openAiResponsesRequest.temperature' },
          { path: 'prompt.openAiResponsesRequest.topP' },
          { path: 'prompt.azureOpenAiResponsesRequest.temperature' },
          { path: 'prompt.azureOpenAiResponsesRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.temperature' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.topP' },
          { path: 'prompt.fireworksInvokeCustomOpenAiRequest.presencePenalty' },
          {
            path: 'prompt.fireworksInvokeCustomOpenAiRequest.frequencyPenalty',
          },
          { path: 'prompt.llamaModelRequest.temperature' },
          { path: 'prompt.llamaModelRequest.topP' },
          { path: 'prompt.runwareTextToImageRequest.cfgScale' },
          { path: 'prompt.runwareTextToImageRequest.loraModels.weight' },
          { path: 'prompt.mlPlatformLlamaModelRequest.temperature' },
          { path: 'prompt.mlPlatformLlamaModelRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.temperature' },
          { path: 'prompt.perplexityChatCompletionRequest.topP' },
          { path: 'prompt.perplexityChatCompletionRequest.topK' },
          { path: 'prompt.perplexityChatCompletionRequest.presencePenalty' },
          { path: 'prompt.perplexityChatCompletionRequest.frequencyPenalty' },
          { path: 'prompt.runwareVideoInferenceRequest.cfgScale' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.api_infra.v1.prompt_proxy',
      method: 'POST' as any,
      methodFqn:
        'wix.api_infra.v1.WixAiExternalGateway.GenerateContentByPromptObjectAsync',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixApiInfraV1WixAiExternalGatewayUrl({
        protoPath: '/v1/generate-content-by-prompt-object-async',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.safetyRatings.probabilityScore',
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.safetyRatings.severityScore',
              },
              {
                path: 'response.googleGenerateImageResponse.predictions.safetyAttributes.scores',
                isRepeated: true,
              },
              { path: 'response.openAiResponsesResponse.temperature' },
              { path: 'response.azureOpenAiResponsesResponse.temperature' },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.temperature',
              },
              { path: 'materializedPrompt.openAiChatCompletionRequest.topP' },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.openAiChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.temperature',
              },
              { path: 'materializedPrompt.azureChatCompletionRequest.topP' },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.azureChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.temperature',
              },
              {
                path: 'materializedPrompt.googleGeminiGenerateContentRequest.generationConfig.topP',
              },
              { path: 'materializedPrompt.anthropicClaudeRequest.temperature' },
              { path: 'materializedPrompt.anthropicClaudeRequest.topP' },
              {
                path: 'materializedPrompt.googleAnthropicClaudeRequest.temperature',
              },
              { path: 'materializedPrompt.googleAnthropicClaudeRequest.topP' },
              {
                path: 'materializedPrompt.invokeAnthropicModelRequest.temperature',
              },
              { path: 'materializedPrompt.invokeAnthropicModelRequest.topP' },
              {
                path: 'materializedPrompt.amazonConverseRequest.inferenceConfig.temperature',
              },
              {
                path: 'materializedPrompt.amazonConverseRequest.inferenceConfig.topP',
              },
              {
                path: 'materializedPrompt.stabilityAiTextToImageRequest.textPrompts.weight',
              },
              {
                path: 'materializedPrompt.stabilityAiStableDiffusionRequest.strength',
              },
              {
                path: 'materializedPrompt.blackForestLabsGenerateImageRequest.imagePromptStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.trueCfg',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.idWeight',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxPulid.guidanceScale',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.loraStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.guidanceScale',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.controlStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.fluxDevControlnet.imageToImageStrength',
              },
              {
                path: 'materializedPrompt.replicateCreatePredictionRequest.prunaaiZImageTurbo.guidanceScale',
              },
              {
                path: 'materializedPrompt.stabilityAiEditWithPromptRequest.creativity',
              },
              { path: 'materializedPrompt.runwareTextToImageRequest.strength' },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.trueCfg',
              },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.idWeight',
              },
              {
                path: 'materializedPrompt.mlPlatformGenerateImageRequest.fluxPulid.guidanceScale',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.temperature',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.topP',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.googleCreateChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.temperature',
              },
              { path: 'materializedPrompt.mlPlatformOpenAiRawRequest.topP' },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.mlPlatformOpenAiRawRequest.frequencyPenalty',
              },
              { path: 'materializedPrompt.openAiResponsesRequest.temperature' },
              { path: 'materializedPrompt.openAiResponsesRequest.topP' },
              {
                path: 'materializedPrompt.azureOpenAiResponsesRequest.temperature',
              },
              { path: 'materializedPrompt.azureOpenAiResponsesRequest.topP' },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.temperature',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.topP',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.fireworksInvokeCustomOpenAiRequest.frequencyPenalty',
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.groundingSupports.confidenceScores',
                isRepeated: true,
              },
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.retrievalMetadata.googleSearchDynamicRetrievalScore',
              },
              { path: 'materializedPrompt.llamaModelRequest.temperature' },
              { path: 'materializedPrompt.llamaModelRequest.topP' },
              { path: 'materializedPrompt.runwareTextToImageRequest.cfgScale' },
              {
                path: 'materializedPrompt.runwareTextToImageRequest.loraModels.weight',
              },
              {
                path: 'materializedPrompt.mlPlatformLlamaModelRequest.temperature',
              },
              { path: 'materializedPrompt.mlPlatformLlamaModelRequest.topP' },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.temperature',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.topP',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.topK',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.presencePenalty',
              },
              {
                path: 'materializedPrompt.perplexityChatCompletionRequest.frequencyPenalty',
              },
              {
                path: 'materializedPrompt.runwareVideoInferenceRequest.cfgScale',
              },
            ],
          },
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [
              {
                path: 'response.googleGeminiGenerateContentResponse.candidates.groundingMetadata.searchEntryPoint.sdkBlob',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __generateContentByPromptObjectAsync;
}

export function generateAudioStreamed(
  payload: object
): RequestOptionsFactory<any> {
  function __generateAudioStreamed({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'openAiCreateSpeechRequest.speed' },
          { path: 'elevenlabsTextToSpeechRequest.voiceSettings.style' },
          { path: 'elevenlabsTextToSpeechRequest.voiceSettings.stability' },
          {
            path: 'elevenlabsTextToSpeechRequest.voiceSettings.similarityBoost',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.api_infra.v1.prompt_proxy',
      method: 'POST' as any,
      methodFqn: 'wix.api_infra.v1.WixAiExternalGateway.GenerateAudioStreamed',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixApiInfraV1WixAiExternalGatewayUrl({
        protoPath: '/v1/generate-audio-streamed',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [
              { path: 'openAiSpeechChunk.content' },
              { path: 'elevenlabsSpeechChunk.audioBase64' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'elevenlabsSpeechChunk.alignment.characterStartTimesSeconds',
                isRepeated: true,
              },
              {
                path: 'elevenlabsSpeechChunk.alignment.characterEndTimesSeconds',
                isRepeated: true,
              },
              {
                path: 'elevenlabsSpeechChunk.normalizedAlignment.characterStartTimesSeconds',
                isRepeated: true,
              },
              {
                path: 'elevenlabsSpeechChunk.normalizedAlignment.characterEndTimesSeconds',
                isRepeated: true,
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __generateAudioStreamed;
}
